import clsx from "clsx";
import type { HTMLAttributes } from "react";

import type { DisplayState } from "./state";

/** Neutral signals that are no element state: "note" is worth knowing, "problem" went wrong or blocks (UI-07). */
export type SignalTone = "note" | "problem";

export interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * A state look (only for an element or a line in that state), a neutral signal (a note, a problem), or "neutral" for a
   * pill that carries no colour. Colour is for state only: a failed check or "Live" is a signal, never a state.
   */
  tone?: DisplayState | SignalTone | "neutral";
  /** Draw the outline dashed. A proposal is always dashed, whatever this says. */
  dashed?: boolean;
}

/** A small rounded label. A problem also carries an icon, so it is not told by colour alone. */
export function Pill({ tone = "neutral", dashed = false, className, children, ...rest }: PillProps) {
  const signal = tone === "note" || tone === "problem" ? tone : undefined;
  return (
    <span
      className={clsx("pill", className)}
      data-state={tone === "neutral" || signal ? undefined : tone}
      data-signal={signal}
      data-dashed={dashed || tone === "proposed" ? "true" : undefined}
      {...rest}
    >
      {signal === "problem" && (
        <span className="pill__icon" aria-hidden="true">
          !
        </span>
      )}
      {children}
    </span>
  );
}
